import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { File, Image as ImageIcon } from 'lucide-react';
import style from './markdown-styles.module.css';
import { extractSourcesFromContent, normalizeFilename } from '../utils/sourceValidator';
import { normalizeAiMarkdownText, remarkNestLabeledListItems, isLabelOnlyLiNode } from '../utils/markdownNormalize';
import { useTranslation } from '../i18n/LanguageContext';

// react-markdown 9 no longer passes `inline`; fenced blocks carry a language class or a newline.
export const isInlineCode = (className, children) => {
  const text = Array.isArray(children) ? children.join('') : String(children ?? '');
  return !/language-/.test(className || '') && !text.includes('\n');
};

// Shared between inline document citations below (SYSTEM_PROMPT instructs the
// model to write them as "[Dateiname.pdf, Seite N](url)" markdown links) and
// ChatBlob's own "Sources" panel underneath the message — same pill, same
// icon, in both places, rather than two independent styles drifting apart.
//
// `onClick`, when provided, always wins over `href` — clicking opens the PDF
// in the app's OWN sidebar viewer (ChatBlob's handleSourceClick: dispatches
// fetchPDF/addSource and calls openSidebar()) instead of a plain <a href>,
// which used to open the browser's native PDF viewer in a brand-new tab —
// the model's own raw href (often just a bare filename/relative path, not a
// real fetchable URL) was never meant to be navigated to directly. `href` is
// kept only as a fallback for contexts with no sidebar to open into (e.g. a
// read-only/shared view rendering this with no onSourceClick supplied).
//
// `compact`: smaller variant for the inline-in-text usage below, matching
// the size of ChatBlob's own inline WEB citations (WebSearchContent's
// citationLink) — the Sources panel keeps the roomier default size.
export function DocumentSourcePill({ filename, page, href, onClick, compact = false, fullWidth = false, folderPath = null }) {
  const { t } = useTranslation();
  const Wrapper = onClick ? 'button' : (href ? 'a' : 'button');
  const wrapperProps = onClick
    ? { type: 'button', onClick }
    : (href ? { href, target: '_blank', rel: 'noopener noreferrer' } : { type: 'button' });
  // Citation-only source, no File object handy — extension is the only
  // signal available here for whether the cited page came from an image.
  const isImage = /\.(jpe?g|png)$/i.test(filename || '');
  // Inline/compact pills stay short (no visible path — would break the flow
  // of a sentence) but the full path is still one hover away in the title
  // tooltip. The roomier Sources-panel variant (below) shows it directly.
  const baseTitle = page != null && page !== '' ? `${filename} — ${t('analysis.pageWithNumber', { page })}` : filename;
  if (compact) {
    return (
      <Wrapper
        {...wrapperProps}
        title={folderPath ? `${folderPath}/${filename}` : baseTitle}
        className="inline-flex items-center gap-1 mx-0.5 px-1.5 py-0.5 text-[11px] font-medium text-[#374151] hover:bg-[#F3F4F6] rounded-md border border-[#E5E7EB] no-underline transition-colors align-middle max-w-[180px]"
        style={{ backgroundColor: '#F9FAFB', lineHeight: 1.4 }}
      >
        {isImage
          ? <ImageIcon size={14} className="text-[#7C3AED] flex-shrink-0" strokeWidth={2} />
          : <File size={14} className="text-[#7C3AED] flex-shrink-0" strokeWidth={2} />}
        <span className="truncate">{filename}</span>
        {page != null && page !== '' && (
          <span className="text-[#9CA3AF] whitespace-nowrap flex-shrink-0">{page}</span>
        )}
      </Wrapper>
    );
  }
  return (
    <Wrapper
      {...wrapperProps}
      title={folderPath ? `${folderPath}/${filename}` : undefined}
      className={`items-center gap-2 pl-1.5 pr-3.5 py-1.5 my-0.5 rounded-lg border border-[#E5E7EB] bg-white no-underline shadow-sm hover:shadow-md hover:border-[#D1D5DB] hover:-translate-y-px transition-all align-middle ${fullWidth ? 'flex w-full' : 'inline-flex max-w-[240px]'}`}
    >
      <span className="w-6 h-6 flex-shrink-0 rounded-[7px] bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center shadow-[0_1px_2px_rgba(109,40,217,0.35)]">
        {isImage
          ? <ImageIcon size={12.5} className="text-white" strokeWidth={2} />
          : <File size={12.5} className="text-white" strokeWidth={2} />}
      </span>
      <span className={`min-w-0 ${fullWidth ? 'flex-1' : ''}`}>
        <span className="block text-[13px] text-[#1F2937] font-medium tracking-[-0.01em] truncate">{filename}</span>
        {folderPath && (
          <span className="block text-[11px] text-[#9CA3AF] truncate">{folderPath}</span>
        )}
      </span>
      {page != null && page !== '' && (
        <span className="text-[12.5px] text-[#9CA3AF] whitespace-nowrap flex-shrink-0 pl-2 border-l border-[#E5E7EB]">
          {t('analysis.pageWithNumber', { page })}
        </span>
      )}
    </Wrapper>
  );
}

const isMarkdown = (text) => {
  if (!text) return false;
  
  // Check for bullet points and numbered lists at the start of lines
  const listPattern = /^(\d+\.\s|[-*+]\s)/m;
  if (listPattern.test(text)) {
    return true;
  }

  // Convert potential numbered list text to proper markdown
  const processNumberedList = (content) => {
    if (!content) return content;
    return content.replace(/^(\d+)[).]\s/gm, '$1. ');
  };

  // Check for other markdown patterns
  const markdownPattern = /(^#{1,6}\s)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(__[^_]+__)|(_[^_]+_)|(\[[^\]]+\]\([^)]+\))|(`[^`]+`)|(\s*[*-]\s+)|(^\d+\.\s)|(^\|.*\|$)|(^>\s)|(^---$)|(!\[[^\]]*\]\([^)]+\))/m;
  return markdownPattern.test(text);
};

// onSourceClick(filename, page): when the caller supplies this (ChatBlob
// passes its own handleSourceClick — opens the PDF in the app's sidebar
// viewer), an inline document citation calls it directly instead of
// navigating to the model's own raw href. See DocumentSourcePill's own
// comment above for why that raw-href fallback used to open a real new
// browser tab.
const ContentRenderer = ({ content, sources, onSourceClick }) => {
    // Safety net for a trailing "Sources:"/"Quellen:" section the backend
    // (question.py's generate_chat_response) is meant to strip from the
    // model's raw answer before saving it — its own split only catches an
    // exact "\nSources:" match, so any markdown-decorated or German variant
    // ("**Sources:**", "## Sources", "Quellen:") slips through and gets
    // persisted as part of the answer. Without this, that duplicated
    // citation list still renders as pills here (via processSourceReferences
    // below) on TOP of the app's own separate Sources panel — this strips it
    // at render time regardless of when the message was saved, covering
    // messages already in the DB from before the backend fix too.
    const stripTrailingSourcesSection = (text) => {
      if (!text) return text;
      // No trailing \n required after the label — the model sometimes writes
      // the whole citation list on the SAME line as "Sources:" (e.g.
      // "Sources: Source: file.pdf, Page 1 Source: file2.pdf, Page 2"),
      // which an earlier version of this regex (requiring \s*\n right after
      // the label) didn't match at all, leaving that entire line as a
      // duplicate plain-text block underneath the message.
      return text.split(/\n+#{0,3}\s*\*{0,2}(?:Sources|Quellen):?\*{0,2}:?\s*/i)[0].trimEnd();
    };

    // Function to process source references with enhanced pattern matching
    const processSourceReferences = (text) => {
      if (!text) return text;
      
      // Extract sources from content using the enhanced utility
      const extractedSources = extractSourcesFromContent(text);
      
      // If no sources found, return original text
      if (extractedSources.length === 0) return text;
      
      // Process the text to ensure source references are properly formatted.
      // Each pattern below is rewritten into a real "[filename, Page N](#)"
      // markdown link — not just re-formatted plain text — so the `a`
      // component override further down (which turns that exact shape into a
      // DocumentSourcePill) picks it up no matter where the citation lands:
      // a normal paragraph, a list item, or a table cell. Previously these
      // patterns were only reformatted as plain text, which is why a
      // citation the model wrote as e.g. "(*file.pdf, Page 6*)" inside a
      // table cell rendered as literal italic text instead of a pill — there
      // was never an actual <a> node for the renderer to intercept. `#` is a
      // harmless placeholder href: onSourceClick (when supplied) always
      // takes over the pill's click behavior and renders it as a <button>
      // instead of a real <a>, so this href is only ever reached as a
      // no-op fallback when no onSourceClick handler exists at all.
      let processedText = text;

      // Every filename group below uses a NON-GREEDY ".+?" (not "[^,]+") —
      // a filename can genuinely contain a comma itself (e.g. an
      // auto-exported image name like "ChatGPT Image 27. Juli 2026,
      // 14_04_01.png"), which made "[^,]+" stop matching at that internal
      // comma and fail the whole pattern, leaving the citation as
      // unconverted plain text. ".+?" instead expands only as far as
      // needed to find the required ".pdf/.png/.jpg" extension immediately
      // before the real ", Page N" suffix, so it correctly walks past any
      // comma that isn't actually the filename/page separator — verified
      // this doesn't merge two separate citations in the same paragraph
      // into one match, since each pattern still requires its own anchored
      // prefix ("[N]", "Source:", "(*") or the specific extension+Page
      // suffix immediately after the captured text.

      // Pattern 1: [N] filename.pdf, Page N
      processedText = processedText.replace(
        /\[(\d+)\]\s+(.+?\.(?:pdf|png|jpe?g)),\s*Page\s*(\d+)/gi,
        (match, refNum, filename, page) => {
          return `[${refNum}] [${normalizeFilename(filename)}, Page ${page}](#)`;
        }
      );

      // Pattern 2: Source: filename.pdf, Page N — also consumes an optional
      // surrounding "*(...)*" wrapper (the model often writes the whole
      // citation as italic-parenthesized, e.g. "*(Source: file.pdf, Page
      // 6)*"), so the "Source:" word and the parens/asterisks are replaced
      // away too, leaving just the pill with nothing decorating it.
      processedText = processedText.replace(
        /\*?\(?Source:\s*(.+?\.(?:pdf|png|jpe?g)),\s*Page\s*(\d+)\s*\)?\*?/gi,
        (match, filename, page) => {
          return `[${normalizeFilename(filename)}, Page ${page}](#)`;
        }
      );

      // Pattern 3: (*filename.pdf, Page N*) — no "Source:" word, just the
      // filename/page wrapped in italic parens. The parens/asterisks are
      // dropped entirely rather than kept around the link, same reasoning
      // as Pattern 2: only the pill itself should show, nothing framing it.
      processedText = processedText.replace(
        /\(\*(.+?\.(?:pdf|png|jpe?g)),\s*Page\s*(\d+)\*\)/gi,
        (match, filename, page) => {
          return `[${normalizeFilename(filename)}, Page ${page}](#)`;
        }
      );

      // Pattern 4: filename.pdf (Page N) — unlike patterns 1-3 above, this one
      // has no distinguishing prefix ("[N]", "Source:", "(*") to re-sync on,
      // so the filename group excludes "(", ")" and newlines (never legal in
      // a real filename) — otherwise ".+?" could run past an earlier
      // already-converted "[...](#)" citation or an earlier "(*...*)"
      // citation entirely, merging two separate citations into one bogus
      // match spanning both.
      processedText = processedText.replace(
        /([^()\n]+?\.(?:pdf|png|jpe?g))\s*\(Page\s*(\d+)\)/gi,
        (match, filename, page) => {
          return `[${normalizeFilename(filename)}, Page ${page}](#)`;
        }
      );

      return processedText;
    };
    
    // Function to convert \n to actual line breaks
    const processLineBreaks = (text) => {
      if (!text) return text;
      // Replace literal \n with actual newlines
      return text.replace(/\\n/g, '\n');
    };
    
    const processedContent = processSourceReferences(stripTrailingSourcesSection(content));

    // Check if content is markdown and render accordingly. Deliberately no
    // language-specific word check (an earlier version looked for the
    // English "Source:" — broke the instant a prompt used any other
    // language's word, e.g. German "Quelle:"). Every check here matches
    // markdown SYNTAX itself, not any particular language's wording, so it
    // holds regardless of what language a given org's SYSTEM_PROMPT is in.
    // The citation links — "[Dateiname.pdf, Seite N](url)" — are sometimes
    // the ONLY markdown syntax an answer contains (no bold/lists/headers
    // anywhere else); without also matching that [text](url) shape, such an
    // answer fell through to the plain-text fallback below, which doesn't
    // parse markdown at all, so the citation showed as literal
    // "[filename](url)" text instead of becoming a link/pill. Checked against
    // `processedContent` (post processSourceReferences), not the raw
    // `content` — processSourceReferences turns plain-text citation shapes
    // like "Source: file.pdf, Page 6" into real "[...](#)" markdown links,
    // so a message whose ONLY markdown-like content is one of those needs
    // the check to see that link syntax, or it never reaches ReactMarkdown
    // at all and the newly-built link is never parsed into a pill.
    const isMarkdownContent = processedContent && (
      processedContent.includes('##') ||
      processedContent.includes('*') ||
      processedContent.includes('```') ||
      processedContent.includes('- ') ||
      processedContent.includes('1. ') ||
      processedContent.includes('|') ||
      /\[[^\]]+\]\([^)]+\)/.test(processedContent)
    );

    const contentWithLineBreaks = processLineBreaks(processedContent);

    // Process the content to ensure proper numbered list formatting
    const numberedListFixed = contentWithLineBreaks ? contentWithLineBreaks.replace(/^(\d+)[).]\s/gm, '$1. ') : '';
    // Same defensive markdown normalization ChatBlob's web-search answers get
    // (bold-wrapped numbers, under-indented sub-bullets) — this renderer was
    // missing it entirely, so any documents-mode answer (or a web/hybrid
    // answer where the model didn't end up calling web search, which also
    // routes here) with these formatting quirks rendered with the exact same
    // "all points at the same level" bug, just silently, since this path
    // never had ANY of that protection.
    const formattedContent = normalizeAiMarkdownText(numberedListFixed);

    // Check if content contains HTML table tags or markdown table syntax
    const hasHtmlTable = contentWithLineBreaks && /<table[\s\S]*?<\/table>/i.test(contentWithLineBreaks);
    const hasMarkdownTable = contentWithLineBreaks && /\|[^\|]+\|/.test(contentWithLineBreaks) && contentWithLineBreaks.includes('\n');

    
    if (hasHtmlTable) {
      // If content has HTML tables, render it as HTML (also convert \n to <br>)
      const htmlContent = contentWithLineBreaks.replace(/\n/g, '<br/>');
      return <div className={style.reactMarkDown} dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    } else if (isMarkdownContent || hasMarkdownTable) {
      return (
        // md-ol-counter (index.css): keeps numbered-list display continuous
        // (1,2,3...) even when the model's answer splits into several
        // separate <ol> blocks — see markdownNormalize.js for the full
        // rationale, shared with ChatBlob's web-search renderer.
        <div className="md-ol-counter">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkNestLabeledListItems]}
          rehypePlugins={[rehypeSanitize]}
          className={style.reactMarkDown}
          components={{
            // SYSTEM_PROMPT instructs the model to cite documents as
            // "[Dateiname.pdf, Seite N](url)" markdown links, rendered as a
            // plain default <a> otherwise. Any org's own prompt can be in any
            // language, so this doesn't match a specific word ("Seite"/
            // "Page"/"Página"/"Strana"/...) — it matches the SHAPE instead:
            // "<filename>, <anything non-numeric><trailing number>". That
            // works for whatever language wrote it, with nothing to update
            // if a new one shows up. The page number extracted this way is
            // only ever used as raw data — the pill's own displayed label
            // still goes through t('analysis.pageWithNumber'), so what's
            // shown is always in the app's own current UI language, not
            // whatever language the citation text happened to be written in.
            // Anything that doesn't match this shape (a genuine unrelated
            // link the model wrote) falls through to a plain link.
            a: ({ href, children }) => {
              const rawText = Array.isArray(children) ? children.join('') : String(children ?? '');
              // The optional "(?:\s*[-–—]\s*\d+)?" tail handles a page RANGE
              // citation ("FYPFast.pdf, Seite 30–31") — without it, "\D*(\d+)"
              // only ever captures the first page number, then requires the
              // string to end right there, so the whole match failed for any
              // range and the citation fell through to a plain link with
              // href="#" (clicking it opened the same chat in a new tab
              // instead of becoming a pill). The inner group still captures
              // just the FIRST page number for navigation (a PDF viewer jumps
              // to one page), while the outer group keeps the full range text
              // exactly as written for display.
              const match = rawText.match(/^(.+),\s*\D*((\d+)(?:\s*[-–—]\s*\d+)?)\s*$/);
              if (match) {
                const filename = match[1].trim();
                const page = match[2];
                const clickPage = match[3];
                // This citation is plain text the model wrote (filename+page
                // only, no item_id) — look the match up in the structured
                // sources list (same one the Sources panel below already
                // uses) to recover it, so two active files sharing this
                // filename still resolve to the exact one that was cited.
                let matchedSource = (sources || []).find(
                  (s) => s?.filename?.trim().toLowerCase() === filename.toLowerCase() && String(s?.page) === String(clickPage)
                );
                if (!matchedSource) {
                  // The model can mangle an unusual filename when writing the
                  // citation (seen live: "pdf mit einem namen b.pdf" shortened
                  // to just "b.pdf") — if exactly one retrieved source has
                  // this page number, there's no other file it could mean, so
                  // resolve to it (and use ITS real filename below) instead of
                  // opening a dead link for text the model half-invented.
                  const samePage = (sources || []).filter((s) => String(s?.page) === String(clickPage));
                  if (samePage.length === 1) matchedSource = samePage[0];
                }
                const displayFilename = matchedSource?.filename || filename;
                return (
                  <DocumentSourcePill
                    filename={displayFilename}
                    page={page}
                    href={href}
                    compact
                    folderPath={matchedSource?.folderPath}
                    onClick={onSourceClick ? () => onSourceClick(displayFilename, clickPage, matchedSource?.itemId) : undefined}
                  />
                );
              }
              return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
            },
            // Label-only items ("- **Pros**", nothing else — see
            // markdownNormalize.js) render bold with no bullet, as a
            // sub-heading for the bullets remarkNestLabeledListItems already
            // nested under them. Every other item keeps this renderer's
            // existing normal-weight style.
            li: ({node, ...props}) => isLabelOnlyLiNode(node)
              ? <li style={{fontWeight: 600, fontSize: '14.4px', listStyle: 'none', marginLeft: '-1.25rem', marginTop: '0.5rem'}} {...props} />
              : <li style={{fontWeight: 'normal', fontSize: '14.4px'}} {...props} />,
            // Always normal weight, unconditionally — this used to check
            // `node.parent`, which plain hast nodes never actually carry (it
            // was always undefined, so the "inside a list item" branch never
            // ran), leaving paragraphs free to inherit bold from whatever
            // ancestor they end up under. That ancestor can now genuinely be
            // a bold label <li> (remarkNestLabeledListItems nests real
            // content underneath it), so this needs to be unconditional.
            p: ({node, ...props}) => <p style={{fontWeight: 'normal', fontSize: '14.4px', overflowWrap: 'break-word'}} {...props} />,
            // No override existed for code blocks at all before this, so
            // react-markdown fell back to a bare <pre><code> — the browser
            // default `white-space: pre` never wraps long lines (a JSON
            // block's "key": "value" lines, for example), so they render at
            // their full intrinsic width. Combined with the message column
            // having no overflow-x guard, that pushed the whole answer box
            // wider than the chat area instead of scrolling inside it.
            // `overflow-x-auto` + `max-w-full` here mirror WebSearchContent's
            // own `code`/`pre` override in ChatBlob.jsx.
            // Font size is an explicit 14.4px, NOT Tailwind's `text-sm` —
            // this app sets `html { font-size: 90% }` globally, so `text-sm`
            // (0.875rem) resolves to ~12.6px here, visibly smaller than the
            // 14.4px every other element in this renderer uses (p/li/table
            // below). That mismatch is exactly the "source code font size
            // looks strange" bug reported against this screen: code blocks
            // rendering at a different size than the surrounding answer text.
            code: ({ inline, children, className, node, ...props }) => (inline ?? isInlineCode(className, children))
              ? <code className="bg-gray-100 px-1 rounded font-mono text-[14.4px]" {...props}>{children}</code>
              : <pre className="bg-gray-100 rounded p-3 font-mono text-[14.4px] overflow-x-auto max-w-full mb-2"><code className={className} {...props}>{children}</code></pre>,
            // Ensure tables are properly styled — wrapped in its own
            // overflow-x-auto container (same fix as the `pre`/code-block
            // override above) so a cell wide enough to push the table past
            // the chat bubble's width (long text, a citation pill) scrolls
            // inside the table instead of spilling out past the sidebar edge.
            table: ({node, ...props}) => (
              <div className="overflow-x-auto max-w-full">
                <table className="border-collapse border border-gray-300 my-4 text-[14.4px]" {...props} />
              </div>
            ),
            thead: ({node, ...props}) => <thead className="bg-gray-100" {...props} />,
            tbody: ({node, ...props}) => <tbody {...props} />,
            tr: ({node, ...props}) => <tr className="border-b border-gray-300" {...props} />,
            th: ({node, ...props}) => <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-[14.4px]" {...props} />,
            td: ({node, ...props}) => <td className="border border-gray-300 px-4 py-2 text-[14.4px]" {...props} />
          }}
        >
          {formattedContent}
        </ReactMarkdown>
        </div>
      );
    }
    
    // For non-markdown content, convert newlines to <br> tags for proper display
    const simpleContentWithBreaks = contentWithLineBreaks ? contentWithLineBreaks.replace(/\n/g, '<br/>') : '';
    return <div className='simple-answer whitespace-pre-wrap' dangerouslySetInnerHTML={{ __html: simpleContentWithBreaks }} />;
  };

export default ContentRenderer;